/**
 * Self-registration for patients and doctors.
 *
 * Merges the patient signup branch of `handlePatientAuth` with
 * `handleDoctorRegistration`. Both hit POST /api/auth/register — the backend
 * accepts exactly these two roles and marks a new doctor `pending`, which is
 * what the doctor path tells the user to expect.
 *
 * المرحلة 1.1: the platform's `emailDomain` policy decides whether any email
 * is accepted or Gmail only, the country profile which mobile numbers are,
 * and — when `consentTexts` asks — the terms and privacy notice in force are
 * accepted here, by version, with the account.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Field,
  Icon,
  Input,
  Panel,
  Select,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { validatePatientSignup, validateDoctorRegistration, MESSAGES } from '../../domain/validation';
import { readFileAsDataUri, compressImage } from '../../domain/files';
import { useI18n } from '../../i18n/I18nProvider';
import { readApplicant, saveApplicant } from '../apply/applicant';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { isMobileOf } from '../../domain/locale';

const ACCOUNT_TYPES = [
  { value: 'patient', label: 'register.patient', icon: 'user' },
  { value: 'doctor', label: 'register.doctor', icon: 'stethoscope' },
];

const EMPTY_FORM = {
  name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  categoryId: '',
  title: '',
  priceExam: 500,
  priceFollowup: 200,
};

export function RegisterPage() {
  const { register } = useAuth();
  const { categories } = useData();
  const navigate = useNavigate();
  const { t, tr, policy, country, lang } = useI18n();

  const [role, setRole] = useState('patient');
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const isDoctor = role === 'doctor';

  const update = (field) => (event) => {
    const value = event?.target?.type === 'number' ? event.target.value : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError(null);
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      // Profile photos go into the DB as base64, so they are compressed first.
      const dataUri = file.type.startsWith('image/')
        ? await compressImage(file, { maxWidth: 600, maxHeight: 600, quality: 0.75 })
        : await readFileAsDataUri(file);
      setImage(dataUri);
    } catch (err) {
      setServerError(t('register.photoFailed', { error: err.message }));
    }
  };

  const validation = useMemo(
    () => (isDoctor ? validateDoctorRegistration : validatePatientSignup),
    [isDoctor],
  );

  // The platform's rules, once GET /api/auth/policy has answered.
  const emailAny = policy?.emailDomain === 'any';
  const foreign = country && country.code && country.code !== 'EG';
  const phoneExample = country?.mobile?.example || '01xxxxxxxxx';
  const countryName = lang === 'en' ? country?.nameEn : country?.name;
  const consentTexts = policy?.consent?.required ? policy.consent.texts || [] : [];
  const titleOf = (text) => (text ? (lang === 'en' && text.titleEn ? text.titleEn : text.title) : '');
  const termsText = consentTexts.find((x) => x.kind === 'terms');
  const privacyText = consentTexts.find((x) => x.kind === 'privacy');

  /** A field's error, in the reader's language (the phone's in the country's format). */
  const errorOf = (field) => {
    const message = errors[field];
    if (!message) return undefined;
    if (field === 'phone' && message === MESSAGES.phoneInvalid && foreign) return t('register.phoneHint', { country: countryName, example: phoneExample });
    return tr(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const rules = { emailAny, ...(foreign ? { phoneOk: (value) => isMobileOf(value, country) } : {}) };
    const result = validation(form, rules);
    if (consentTexts.length && !agreed) result.errors.consent = t('consent.needed');
    setErrors(result.errors);
    if (!result.valid || result.errors.consent) return;

    setSubmitting(true);
    setServerError(null);

    try {
      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        phone: form.phone.trim(),
        role,
        // The versions read and accepted on this form (المرحلة 1.1).
        ...(consentTexts.length && agreed ? { acceptConsents: consentTexts.map((text) => text.id) } : {}),
      };

      if (isDoctor) {
        Object.assign(payload, {
          categoryId: form.categoryId,
          title: form.title.trim() || t('register.jobTitleDefault'),
          priceExam: Number(form.priceExam) || 500,
          priceFollowup: Number(form.priceFollowup) || 200,
          image,
        });
      }

      const result = await register(payload);
      // المرحلة 1.3: a new doctor goes on to send their documents.
      if (isDoctor && result?.applicant?.token) saveApplicant(result.applicant.token);

      setDone(isDoctor ? t('register.doneDoctor') : t('register.donePatient'));

      if (!isDoctor) setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      if (err.body?.field === 'consent') setErrors((current) => ({ ...current, consent: t('consent.needed') }));
      setServerError(err.message || t('register.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center app-backdrop p-4">
        <Panel className="w-full max-w-md text-center">
          <Icon name="check-circle-2" size={48} className="mx-auto mb-4 text-emerald-400" />
          <h2 className="mb-2 text-lg font-bold text-white">{t('register.doneTitle')}</h2>
          <p className="mb-6 text-sm leading-relaxed text-slate-300">{done}</p>
          {isDoctor && readApplicant() ? (
            <div className="flex flex-wrap justify-center gap-2">
              <Link to="/apply" className="btn-primary w-fit" data-testid="go-apply">
                {t('apply.continue')}
              </Link>
              <Link to="/login" className="btn-ghost w-fit">
                {t('register.goSignIn')}
              </Link>
            </div>
          ) : (
            <Link to="/login" className="btn-primary mx-auto w-fit">
              {t('register.goSignIn')}
            </Link>
          )}
        </Panel>
      </div>
    );
  }

  return (
    <div className="auth-screen flex min-h-screen items-center justify-center app-backdrop p-4">
      <div className="auth-screen__lang">
        <LanguageSwitch />
      </div>
      <div className="w-full max-w-lg py-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-400">
            <Icon name="user-plus" size={28} />
          </div>
          <h1 className="text-xl font-bold text-white">{t('register.title')}</h1>
        </div>

        <Panel>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Account type */}
            <div className="grid grid-cols-2 gap-2">
              {ACCOUNT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setRole(type.value);
                    setErrors({});
                  }}
                  className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-bold transition-colors ${
                    role === type.value
                      ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                      : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon name={type.icon} size={16} />
                  {t(type.label)}
                </button>
              ))}
            </div>

            <Field label={t('register.name')} error={errorOf('name')} required>
              <Input value={form.name} onChange={update('name')} placeholder={t('register.namePlaceholder')} />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('register.username')} error={errorOf('username')} required>
                <Input value={form.username} onChange={update('username')} autoComplete="username" />
              </Field>

              <Field label={t('register.password')} error={errorOf('password')} required>
                <Input
                  type="password"
                  value={form.password}
                  onChange={update('password')}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label={t('register.email')}
                error={errorOf('email')}
                required={!isDoctor}
                hint={!isDoctor && !emailAny ? t('register.gmailHint') : undefined}
              >
                <Input
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  dir="ltr"
                  placeholder={emailAny ? 'name@example.com' : 'name@gmail.com'}
                />
              </Field>

              <Field
                label={t('register.phone')}
                error={errorOf('phone')}
                hint={foreign ? t('register.phoneHint', { country: countryName, example: phoneExample }) : undefined}
              >
                <Input value={form.phone} onChange={update('phone')} dir="ltr" placeholder={phoneExample} />
              </Field>
            </div>

            {isDoctor && (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t('register.specialty')} error={errorOf('categoryId')} required>
                    <Select value={form.categoryId} onChange={update('categoryId')}>
                      <option value="">{t('register.pickSpecialty')}</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label={t('register.jobTitle')} hint={t('register.jobTitleHint')}>
                    <Input value={form.title} onChange={update('title')} placeholder={t('register.jobTitleDefault')} />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t('register.priceExam')} error={errorOf('priceExam')}>
                    <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
                  </Field>

                  <Field label={t('register.priceFollowup')} error={errorOf('priceFollowup')}>
                    <Input
                      type="number"
                      min="0"
                      value={form.priceFollowup}
                      onChange={update('priceFollowup')}
                    />
                  </Field>
                </div>

                <Field label={t('register.photo')}>
                  <div className="flex items-center gap-3">
                    {image && (
                      <img
                        src={image}
                        alt={t('register.preview')}
                        className="h-14 w-14 rounded-xl border border-white/10 object-cover"
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImage}
                      className="flex-1 text-xs text-slate-400 file:ms-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
                    />
                  </div>
                </Field>

                <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-200">
                  <Icon name="info" size={13} className="me-1 inline" />
                  {t('register.doctorNote')}
                </p>
              </>
            )}

            {serverError && (
              <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300">
                <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
                <span>{serverError}</span>
              </div>
            )}

            {consentTexts.length > 0 && (
              <div>
                <label className={`consent-text__tick consent-text !mt-0 ${agreed ? 'is-ticked' : ''}`} data-testid="register-consent">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(event) => {
                      setAgreed(event.target.checked);
                      setErrors((current) => ({ ...current, consent: undefined }));
                    }}
                  />
                  <span>{t('consent.registerLine', { terms: titleOf(termsText), privacy: titleOf(privacyText) })}</span>
                </label>
                {errors.consent && <p className="mt-1.5 text-xs font-bold text-red-400">{errors.consent}</p>}
              </div>
            )}

            <Button type="submit" className="w-full" loading={submitting} icon="user-plus">
              {t('register.submit')}
            </Button>

            <p className="text-center text-xs text-slate-400">
              {t('register.haveAccount')}{' '}
              <Link to="/login" className="font-bold text-cyan-400 hover:text-cyan-300">
                {t('register.signIn')}
              </Link>
            </p>
          </form>
        </Panel>
      </div>
    </div>
  );
}

export default RegisterPage;
